import { ehNativo, plataforma } from 'services/plataforma';

/**
 * Sair do site sem sair do app.
 *
 * <h2>O problema</h2>
 * Na web, "abrir fora" é `window.open(url, '_blank')`: nasce uma aba, a nossa continua viva atrás
 * dela e o usuário volta pelo gerenciador de abas. Dentro do app não existe nada disso. Um
 * `window.open` com `_blank` numa WebView ou é ignorado ou carrega o destino POR CIMA do bundle — e
 * como não há barra de endereço nem botão de voltar do navegador, a pessoa fica presa fora do app,
 * sem caminho de retorno que não seja matá-lo.
 *
 * O pior caso do projeto era o checkout da Asaas: quem clicava em "Pagar com cartão" pagava e
 * ficava do lado de fora, com a assinatura ativa e a tela de confirmação inalcançável.
 *
 * <h2>Os dois modos, e quando usar cada um</h2>
 * `abrirExterno` abre o navegador DE DENTRO do app (Custom Tabs no Android,
 * SFSafariViewController no iOS): uma camada por cima, com botão de fechar, que devolve ao app
 * exatamente onde estava. É o certo para conteúdo web que a pessoa vai ler ou preencher e voltar —
 * o checkout, os Termos, a Política.
 *
 * `abrirEmAppNativo` tenta entregar o endereço a OUTRO aplicativo do aparelho (mapas, WhatsApp) e
 * cai no `abrirExterno` quando nenhum atende. É o certo quando existe um app melhor que a página:
 * o WhatsApp pelo esquema `whatsapp://` abre a conversa direto, enquanto `wa.me` no navegador
 * embutido carrega uma landing page pedindo para abrir o WhatsApp.
 *
 * Na web os dois são o mesmo `window.open` de sempre — o comportamento atual do site não muda em
 * nada, e é por isso que estas funções podem substituir os `window.open` existentes sem ressalva.
 */

/**
 * A origem pública do site, para quando um caminho relativo não serve — dentro do app, "/termos-de-uso"
 * resolveria para o bundle local.
 *
 * Derivada da base da API em vez de escrita à mão: no build nativo ela já é
 * `https://www.novafaxina.com.br/api` (ver o script build:native), e um segundo lugar com o domínio
 * cravado é um segundo lugar para esquecer no dia em que o domínio mudar. Na web o valor é `/api`,
 * a expressão devolve string vazia e o caminho relativo continua valendo, que é o certo lá.
 */
const ORIGEM_PUBLICA = String(process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');

/** Transforma "/termos-de-uso" em URL absoluta quando estamos no app; na web devolve como veio. */
export function urlPublica(caminho) {
	if (!ehNativo()) return caminho;
	return `${ORIGEM_PUBLICA}${caminho}`;
}

/**
 * Abre uma URL fora da aplicação, mas sem tirar a pessoa do app.
 *
 * @param {string} url
 * @param {{ aoFechar?: () => void }} opcoes `aoFechar` roda quando o navegador embutido é fechado —
 *        é o gancho de "a pessoa voltou", usado pelo checkout para reconsultar a cobrança em vez de
 *        esperar o próximo ciclo do polling.
 */
export async function abrirExterno(url, { aoFechar } = {}) {
	if (!url) return;

	if (!ehNativo()) {
		// Chamado de dentro do handler de clique, então conta como gesto do usuário e nenhum
		// bloqueador de popup pega.
		window.open(url, '_blank', 'noopener,noreferrer');
		aoFechar?.();
		return;
	}

	// mailto:, tel: e afins não são páginas — o navegador embutido não sabe o que fazer com eles e
	// abriria uma aba vazia. Quem resolve é o sistema, entregando ao cliente de e-mail ou ao
	// discador.
	if (!/^https?:/i.test(url)) {
		const { AppLauncher } = await import('@capacitor/app-launcher');
		await AppLauncher.openUrl({ url });
		aoFechar?.();
		return;
	}

	const { Browser } = await import('@capacitor/browser');

	if (aoFechar) {
		// once: o listener sai sozinho depois de disparar. Sem isso, abrir o checkout duas vezes
		// deixaria dois inscritos e a segunda volta reconsultaria a cobrança em dobro.
		const inscricao = await Browser.addListener('browserFinished', () => {
			inscricao.remove();
			aoFechar();
		});
	}

	await Browser.open({ url });
}

/**
 * Entrega o endereço a um aplicativo do aparelho, caindo no navegador embutido se não houver um.
 *
 * @param {{ android?: string, ios?: string, web: string }} enderecos esquemas por plataforma mais a
 *        URL de fallback. `web` é obrigatório porque ele é a resposta quando nada mais atende — e o
 *        caso "nada atende" é comum: aparelho sem WhatsApp, sem app de mapas, ou o usuário tendo
 *        removido o padrão.
 */
export async function abrirEmAppNativo(enderecos) {
	if (!ehNativo()) return abrirExterno(enderecos.web);

	const esquema = plataforma() === 'ios' ? enderecos.ios : enderecos.android;

	if (esquema) {
		try {
			const { AppLauncher } = await import('@capacitor/app-launcher');
			const { completed } = await AppLauncher.openUrl({ url: esquema });
			if (completed) return;
		} catch {
			// Sem app que atenda ao esquema, o launcher rejeita. Não é erro para o usuário: é a
			// deixa para o fallback abaixo, que resolve a mesma intenção pela web.
		}
	}

	return abrirExterno(enderecos.web);
}

/**
 * Endereços de mapa para um destino já formatado por services/mapsService.
 *
 * `geo:0,0?q=` é o esquema padrão do Android para "mostre este lugar": ele deixa o SISTEMA escolher
 * o app de mapas, respeitando quem usa Waze ou Maps.me. No iOS não há equivalente aberto, então vai
 * o esquema do Apple Maps, que está sempre presente.
 */
export function enderecosDeMapa(urlWeb, consulta) {
	const busca = encodeURIComponent(consulta);
	return {
		android: `geo:0,0?q=${busca}`,
		ios: `maps://?q=${busca}`,
		web: urlWeb,
	};
}

/**
 * Endereços de WhatsApp a partir de um link wa.me.
 *
 * `whatsapp://send?phone=` abre a conversa direto. O `wa.me` no navegador embutido carrega uma
 * landing page do WhatsApp que pede para abrir o WhatsApp — um passo a mais para chegar no mesmo
 * lugar, e que dentro do app parece um beco sem saída.
 */
export function enderecosDeWhatsapp(urlWaMe) {
	const telefone = String(urlWaMe || '').match(/wa\.me\/(\d+)/)?.[1];
	if (!telefone) return { web: urlWaMe };

	const esquema = `whatsapp://send?phone=${telefone}`;
	return { android: esquema, ios: esquema, web: urlWaMe };
}
